import { useCallback } from 'react';
import { useDataChannel, useLocalParticipant } from '@livekit/components-react';
import type { Participant } from 'livekit-client';

import { i18n } from '../../i18n';
import { notifyError } from '../../shared/notify';

export const REACTIONS = [
    { id: 'like', emoji: '👍', sound: 'thumbs-up' },
    { id: 'love', emoji: '❤️', sound: 'love' },
    { id: 'clap', emoji: '👏', sound: 'applause' },
    { id: 'laugh', emoji: '😂', sound: 'laughter' },
    { id: 'surprised', emoji: '😮', sound: 'surprise' },
    { id: 'party', emoji: '🎉', sound: 'applause' },
    { id: 'thinking', emoji: '🤔', sound: 'crickets' },
    { id: 'dislike', emoji: '👎', sound: 'boo' },
] as const satisfies readonly { id: string; emoji: string; sound: string }[];

export type ReactionId = (typeof REACTIONS)[number]['id'];

export const REACTION_TOPIC = 'meet.reaction';
export const REACTION_THROTTLE_MS = 300;

type LocalReactionListener = (reactionId: ReactionId, participant: Participant) => void;

const localReactionListeners = new Set<LocalReactionListener>();
let lastSentAt = 0;

export function onLocalReaction(listener: LocalReactionListener) {
    localReactionListeners.add(listener);
    return () => {
        localReactionListeners.delete(listener);
    };
}

export function useSendReaction() {
    const { localParticipant } = useLocalParticipant();
    const { send } = useDataChannel(REACTION_TOPIC);

    return useCallback(
        (reactionId: string) => {
            const reaction = REACTIONS.find(({ id }) => id === reactionId);
            const now = Date.now();
            if (!reaction || now - lastSentAt < REACTION_THROTTLE_MS) return;
            lastSentAt = now;
            for (const listener of localReactionListeners) listener(reaction.id, localParticipant);
            send(new TextEncoder().encode(JSON.stringify({ id: reaction.id })), { reliable: false }).catch((error: unknown) => {
                notifyError(i18n.t('reactions:sendError'), error);
            });
        },
        [localParticipant, send]
    );
}
